import React, { useCallback, useEffect, useState } from 'react';
import { Button, SearchInput } from '../ui';
import { listAssets, listAssetTags, updateAsset, assetUrl } from '../../services/pimApi';

/*
 * Browse-and-reuse picker over the shared asset library (pim_asset). Lets an
 * editor search by filename, filter by tag, edit an asset's tags inline, and
 * select one or more existing assets to attach to the current product.
 *
 * Selection is returned to the caller as asset objects shaped
 * { asset_id, code, mime, original_filename } — the shape MediaManager.persist()
 * and its tiles consume — never bare ids.
 */
export default function MediaLibrary({ single, attachedIds = [], onClose, onSelect }) {
  const [items, setItems] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [q, setQ] = useState('');
  const [activeTags, setActiveTags] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // asset id whose tags are being edited
  const [tagDraft, setTagDraft] = useState('');
  const [error, setError] = useState(null);

  const refreshTags = useCallback(() => {
    listAssetTags().then(setAllTags).catch(() => setAllTags([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 120 };
      if (q.trim()) params.q = q.trim();
      if (activeTags.length) params.tag = activeTags;
      const data = await listAssets(params);
      setItems(data.items || []);
    } catch (e) {
      setError('Failed to load library');
    } finally {
      setLoading(false);
    }
  }, [q, activeTags]);

  useEffect(() => { refreshTags(); }, [refreshTags]);

  // Debounce the text query; tag filters apply immediately.
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const toggleTag = (tag) =>
    setActiveTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));

  const toggleSelect = (id) =>
    setSelected((prev) => {
      const next = single ? new Set() : new Set(prev);
      if (prev.has(id) && !single) next.delete(id);
      else next.add(id);
      return next;
    });

  const startEdit = (asset) => {
    setEditing(asset.id);
    setTagDraft((asset.tags || []).join(', '));
  };

  const saveTags = async (asset) => {
    const tags = tagDraft.split(',').map((s) => s.trim()).filter(Boolean);
    try {
      const updated = await updateAsset(asset.id, { tags });
      setItems((prev) => prev.map((a) => (a.id === asset.id ? { ...a, tags: updated.tags } : a)));
      setEditing(null);
      refreshTags();
    } catch (e) {
      setError('Failed to save tags');
    }
  };

  const confirm = () => {
    const chosen = items
      .filter((a) => selected.has(a.id))
      .map((a) => ({
        asset_id: a.id,
        code: a.code,
        mime: a.mime,
        original_filename: a.original_filename,
      }));
    onSelect(chosen);
  };

  return (
    <div className="ui-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="pim-lib"
        role="dialog"
        aria-modal="true"
        aria-label="Media library"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pim-lib-head">
          <h3>Media library</h3>
          <SearchInput value={q} onChange={setQ} placeholder="Search filename…" />
          <button type="button" className="pim-lib-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {allTags.length > 0 && (
          <div className="pim-lib-tags">
            {allTags.map((tag) => (
              <button
                key={tag}
                type="button"
                className={`pim-tag-chip ${activeTags.includes(tag) ? 'active' : ''}`}
                onClick={() => toggleTag(tag)}
              >
                {tag}
              </button>
            ))}
          </div>
        )}

        <div className="pim-lib-body">
          {loading ? (
            <p className="pim-muted">Loading…</p>
          ) : items.length === 0 ? (
            <p className="pim-muted">No assets match the current filters.</p>
          ) : (
            <div className="pim-lib-grid">
              {items.map((a) => {
                const isSelected = selected.has(a.id);
                const inUse = attachedIds.includes(a.id);
                return (
                  <div key={a.id} className={`pim-lib-tile ${isSelected ? 'selected' : ''}`}>
                    <div
                      className="pim-lib-thumb"
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleSelect(a.id)}
                      onKeyDown={(e) => { if (e.key === 'Enter') toggleSelect(a.id); }}
                    >
                      <img src={assetUrl(a.id)} alt={a.original_filename || a.code} />
                      {(a.tags || []).includes('ai-generated') && (
                        <span className="pim-media-ai-badge" title="AI-generated image">AI</span>
                      )}
                      {isSelected && <span className="pim-lib-check">✓</span>}
                      {inUse && <span className="pim-lib-inuse">In use</span>}
                    </div>
                    <div className="pim-lib-meta">
                      <span className="pim-lib-name" title={a.original_filename || a.code}>
                        {a.original_filename || a.code}
                      </span>
                      {editing === a.id ? (
                        <div className="pim-lib-tagedit">
                          <input
                            type="text"
                            value={tagDraft}
                            autoFocus
                            placeholder="comma, separated, tags"
                            onChange={(e) => setTagDraft(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') saveTags(a); }}
                          />
                          <button type="button" className="pim-mini-btn" onClick={() => saveTags(a)}>Save</button>
                        </div>
                      ) : (
                        <div className="pim-lib-tagrow">
                          {(a.tags || []).map((t) => <span key={t} className="pim-tag">{t}</span>)}
                          <button type="button" className="pim-lib-tagbtn" onClick={() => startEdit(a)}>
                            {(a.tags || []).length ? 'edit tags' : '+ tags'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {error && <div className="pim-error">{error}</div>}
        </div>

        <div className="pim-lib-foot">
          <span className="pim-muted">{selected.size} selected</span>
          <div className="pim-lib-foot-actions">
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button onClick={confirm} disabled={selected.size === 0}>
              {single ? 'Use image' : `Use selected (${selected.size})`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
